




























































































import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import TermsDialog from '@/components/TermsDialog.vue';
import { EmailVerificationResend } from '@/api';
import { authModule } from '@/store';
import ResendCooldown from '@/mixins/ResendCooldown';
import AcceptTerms from '@/mixins/AcceptTerms';

/**
 * What a bidder sees when the server refuses them for `verification_required`.
 *
 * One component for every refusal - bidding and profile edits both open it, so
 * there is one wording and one resend button to keep working. Open it only on
 * that code, never on a status: the bid endpoint answers 400 and the middleware
 * 403, and an ordinary 400 on a bid is a real validation error that belongs
 * inline.
 *
 * The refusal deliberately does not say which obligation is missing, so the
 * steps below are driven by the user resource - `isVerified` and
 * `termsAcceptanceRequired` (see FRONTEND-LOG "Phase 4 - bid gating"). Both can
 * be outstanding at once, which is the normal case for an older account, so
 * both steps can show together.
 *
 * It does not retry the bid afterwards. The user finishes in their inbox, at
 * their own pace, and places the bid again when they are ready - a queued bid
 * firing minutes later, at a price that has moved, would be worse.
 */
@Component({
  components: { TermsDialog },
})
export default class VerificationGateDialog extends Mixins(
  ResendCooldown,
  AcceptTerms,
) {
  @Prop({ default: false }) value: boolean;

  /** Wording only: what the user was trying to do when they were refused. */
  @Prop({ default: 'bid' }) context: 'bid' | 'profile';

  showTerms: boolean = false;

  loading: boolean = false;

  message: string = '';

  messageType: string = 'info';

  get open(): boolean {
    return this.value;
  }

  set open(value: boolean) {
    this.$emit('input', value);
  }

  get email(): string {
    const user = authModule.user;
    return user ? user.email : '';
  }

  get needsVerification(): boolean {
    const user = authModule.user;
    // Defaults to showing the step: if the field is missing for any reason,
    // offering a resend is harmless, while hiding it strands the user with a
    // refusal and nothing to do about it.
    return !user || !user.isVerified;
  }

  get needsTerms(): boolean {
    const user = authModule.user;
    return !!user && !!user.termsAcceptanceRequired;
  }

  get title(): string {
    return this.context === 'profile'
      ? 'Finish setting up your account'
      : 'Before you can bid';
  }

  get intro(): string {
    const what =
      this.context === 'profile'
        ? 'We could not save your details.'
        : 'We could not place your bid.';
    return this.needsVerification && this.needsTerms
      ? `${what} There are two things left to do on your account.`
      : `${what} There is one thing left to do on your account.`;
  }

  @Watch('value')
  valueChanged(open: boolean) {
    if (open) {
      // A fresh dialog starts with no stale message from a previous refusal,
      // but keeps any cooldown that is still running - the server's rate limit
      // does not reset just because this was closed and reopened.
      this.message = '';
      this.acceptError = '';
    }
  }

  async resend() {
    if (this.cooldownActive) {
      return;
    }
    this.loading = true;
    this.message = '';
    try {
      await EmailVerificationResend.request(this.email);
      // The endpoint reports generic success whether or not the address exists
      // or is already verified, so this message must not claim more than that.
      this.messageType = 'info';
      this.message =
        'If that address needs confirming, another email is on its way.';
    } catch (e) {
      const status = e.response ? e.response.status : null;
      if (status === 429) {
        this.messageType = 'warning';
        this.message = 'Please wait a moment before requesting another email.';
      } else {
        this.messageType = 'error';
        this.message = 'That did not work just now. Please try again shortly.';
      }
    } finally {
      this.loading = false;
      // Every outcome starts the countdown, including the failures - a request
      // that errored is exactly the one a user retries in a hurry.
      this.startCooldown();
    }
  }

  async accept() {
    await this.acceptTerms();
    // Closed either way, so the error is visible on the card behind it. The
    // step disappears on success because `termsAcceptanceRequired` is now false.
    this.showTerms = false;
  }

  close() {
    this.open = false;
  }
}
